import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import { DocumentScannerOverlayProps } from "../../@types/states/WebCheckIn";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

function cropToFrame(
  sourceCanvas: HTMLCanvasElement,
  frameRect: { x: number; y: number; w: number; h: number }
): string {
  const { x, y, w, h } = frameRect;
  const out = document.createElement("canvas");
  const scale = 2;
  out.width = w * scale;
  out.height = h * scale;
  const ctx = out.getContext("2d")!;
  ctx.drawImage(sourceCanvas, x, y, w, h, 0, 0, w * scale, h * scale);
  return out.toDataURL("image/jpeg", 0.92);
}

const DocumentScannerOverlay: React.FC<DocumentScannerOverlayProps> = ({
  label,
  onCapture,
  onClose,
  uploadFile, // This is the File object from the gallery
}) => {
  const { t: translate } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number>(0);
  const confidenceRef = useRef<number>(0);

  const [phase, setPhase] = useState<"scanning" | "preview">(
    uploadFile ? "preview" : "scanning"
  );

  const [capturedDataUrl, setCapturedDataUrl] = useState<string>("");
  const [cameraFacing, setCameraFacing] = useState<"environment" | "user">(
    "environment"
  );
  const [objectDetected, setObjectDetected] = useState<boolean>(false);

  // FIX: Create a URL for the uploaded file so it can be displayed in an <img> tag
  const filePreviewUrl = useMemo(() => {
    if (uploadFile) return URL.createObjectURL(uploadFile);
    return "";
  }, [uploadFile]);

  const statusColor = objectDetected ? "#00C896" : "#FF3B30";
  const frameBorderColor = objectDetected ? "#00C896" : "rgba(255,255,255,0.3)";

  const startCamera = useCallback(async (facing: "environment" | "user") => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("Camera error", err);
    }
  }, []);

  useEffect(() => {
    if (phase === "scanning") startCamera(cameraFacing);
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [cameraFacing, startCamera, phase]);

  // Object Detection Logic (Contrast Based)
  useEffect(() => {
    if (phase !== "scanning") return;
    let lastCheck = 0;

    const tick = (timestamp: number) => {
      animFrameRef.current = requestAnimationFrame(tick);
      if (timestamp - lastCheck < 100) return;
      lastCheck = timestamp;

      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;

      // CHANGED: Adjusted detection canvas to be horizontal (80x60 instead of 60x80)
      canvas.width = 80;
      canvas.height = 60;

      // CHANGED: Sampling area adjusted for a horizontal rectangle
      // We take a wider section from the middle
      ctx.drawImage(
        video,
        video.videoWidth * 0.1, // Start 10% from left
        video.videoHeight * 0.35, // Start 35% from top
        video.videoWidth * 0.8, // Take 80% width
        video.videoHeight * 0.3, // Take 30% height
        0,
        0,
        canvas.width,
        canvas.height
      );

      const imageData = ctx.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
      ).data;
      let brightnessSum = 0;
      let varianceSum = 0;
      const pixelCount = canvas.width * canvas.height;

      for (let i = 0; i < imageData.length; i += 4) {
        brightnessSum +=
          (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
      }
      const avgBrightness = brightnessSum / pixelCount;

      for (let i = 0; i < imageData.length; i += 4) {
        const pixelBright =
          (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
        varianceSum += Math.pow(pixelBright - avgBrightness, 2);
      }
      const stdDev = Math.sqrt(varianceSum / pixelCount);

      // UPDATED: High threshold (35) ensures empty tables stay RED
      const isActuallyPresent =
        stdDev > 35 && avgBrightness > 40 && avgBrightness < 235;

      if (isActuallyPresent) {
        confidenceRef.current = Math.min(confidenceRef.current + 1, 5);
      } else {
        confidenceRef.current = Math.max(confidenceRef.current - 1, 0);
      }
      setObjectDetected(confidenceRef.current >= 4);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [phase]);

  const handleCapture = useCallback(() => {
    // Optimization: Allow capture if needed, but logic prefers detection
    const video = videoRef.current;
    if (!video) return;

    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = video.videoWidth;
    tmpCanvas.height = video.videoHeight;
    const ctx = tmpCanvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0);

    // Check if the current layout window is in landscape mode
    const isLandscape = window.innerWidth > window.innerHeight;

    // Dynamically match the exact UI framing dimensions
    const marginX = isLandscape
      ? tmpCanvas.width * 0.25 // 25% side margins in landscape
      : tmpCanvas.width * 0.05; // 5% side margins in portrait

    const marginY = isLandscape
      ? tmpCanvas.height * 0.15 // 15% top/bottom margins in landscape
      : tmpCanvas.height * 0.35; // 35% top/bottom margins in portrait

    const fw = tmpCanvas.width - marginX * 2;
    const fh = tmpCanvas.height - marginY * 2;

    const cropped = cropToFrame(tmpCanvas, {
      x: marginX,
      y: marginY,
      w: fw,
      h: fh,
    });
    setCapturedDataUrl(cropped);
    setPhase("preview");
  }, []);

  const handleConfirm = useCallback(() => {
    if (uploadFile) {
      onCapture(uploadFile);
    } else if (capturedDataUrl) {
      onCapture(dataURLtoFile(capturedDataUrl, `scanned_${Date.now()}.jpg`));
    }
  }, [capturedDataUrl, onCapture, uploadFile]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "#000",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)",
        }}
      >
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ color: "#fff", fontWeight: 600 }}>{label}</Typography>
        <IconButton
          onClick={() =>
            setCameraFacing((p) =>
              p === "environment" ? "user" : "environment"
            )
          }
          sx={{ color: "#fff" }}
        >
          <FlipCameraIosIcon />
        </IconButton>
      </Box>

      {phase === "scanning" ? (
        <>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              border: `2px solid ${frameBorderColor}`,
              boxShadow: objectDetected ? `0 0 20px #00C896` : "none",
              borderRadius: "12px",
              transition: "all 0.3s ease",
              left: "5%",
              right: "5%",
              top: "35%",
              bottom: "35%",
              "@media (orientation: landscape)": {
                left: "25%",
                right: "25%",
                top: "15%",
                bottom: "15%",
              },
            }}
          >
            {/* Corner Accents */}
            {[
              {
                top: -2,
                left: -2,
                borderTop: 6,
                borderLeft: 6,
                bRadius: "12px 0 0 0",
              },
              {
                top: -2,
                right: -2,
                borderTop: 6,
                borderRight: 6,
                bRadius: "0 12px 0 0",
              },
              {
                bottom: -2,
                left: -2,
                borderBottom: 6,
                borderLeft: 6,
                bRadius: "0 0 0 12px",
              },
              {
                bottom: -2,
                right: -2,
                borderBottom: 6,
                borderRight: 6,
                bRadius: "0 0 12px 0",
              },
            ].map((corner, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  width: 30,
                  height: 30,
                  top: corner.top,
                  left: corner.left,
                  right: corner.right,
                  bottom: corner.bottom,
                  borderTop: corner.borderTop
                    ? `${corner.borderTop}px solid ${statusColor}`
                    : "none",
                  borderLeft: corner.borderLeft
                    ? `${corner.borderLeft}px solid ${statusColor}`
                    : "none",
                  borderRight: corner.borderRight
                    ? `${corner.borderRight}px solid ${statusColor}`
                    : "none",
                  borderBottom: corner.borderBottom
                    ? `${corner.borderBottom}px solid ${statusColor}`
                    : "none",
                  borderRadius: corner.bRadius,
                }}
              />
            ))}
          </Box>
          <Typography
            sx={{
              position: "absolute",
              bottom: "20%",
              "@media (orientation: landscape)": {
                bottom: "5%",
              },
              color: statusColor,
              fontWeight: 700,
              textShadow: "0px 2px 4px rgba(0,0,0,0.5)",
            }}
          >
            {objectDetected ? translate("READY") : translate("ALIGN DOCUMENT")}
          </Typography>
        </>
      ) : (
        // PREVIEW PHASE: Displays EITHER the captured photo OR the uploaded file
        <Box
          component="img"
          src={capturedDataUrl || filePreviewUrl}
          sx={{
            maxWidth: "95%",
            maxHeight: "75%",
            borderRadius: 2,
            border: "2px solid #00C896",
            objectFit: "contain",
            // Additional styling safety fallback for preview image rendering
            "@media (orientation: landscape)": {
              maxHeight: "65%",
            },
          }}
        />
      )}

      {/* Footer Controls */}
      <Box
        sx={{
          position: "absolute",
          bottom: 40,
          display: "flex",
          gap: 3,
          "@media (orientation: landscape)": {
            bottom: 15,
          },
        }}
      >
        {phase === "scanning" ? (
          <Box
            onClick={handleCapture}
            sx={{
              width: 75,
              height: 75,
              borderRadius: "50%",
              border: `4px solid #fff`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              bgcolor: "rgba(255,255,255,0.2)",
            }}
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                bgcolor: "#fff",
              }}
            />
          </Box>
        ) : (
          <>
            <Button
              onClick={() => {
                setCapturedDataUrl("");
                setPhase("scanning");
              }}
              startIcon={<ReplayIcon />}
              sx={{ color: "#fff", border: "1px solid #fff", px: 3 }}
            >
              {translate("Retake")}
            </Button>
            <Button
              onClick={handleConfirm}
              startIcon={<CheckIcon />}
              sx={{
                bgcolor: "#00C896",
                color: "#fff",
                px: 3,
                "&:hover": { bgcolor: "#00ab80" },
              }}
            >
              {translate("Use This")}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DocumentScannerOverlay;
